import BigNumber from "bignumber.js"
import {
  PortPredictorVesselSpeed,
  PortPredictorVesselStateDataWithMajorCargo,
} from "modules/PortPredictor/hooks/usePortPredictorAPI"
import {
  PortPredictorJourneyMarker,
  PortPredictorMarkerArrival,
  PortPredictorMarkerDeparture,
  VesselStateLadenOrBallast,
} from "modules/PortPredictor/portPredictorReducer"
import { ChangeEvent, useEffect, useState } from "react"
import {
  Button,
  Form,
  InputGroup,
  OverlayTrigger,
  Popover,
} from "react-bootstrap"
import Select, { SingleValue } from "react-select"
import { SpeedDataAvgKey, getVesselSpeedDataWithAverages } from "./helpers"
import { getIsChosenVesselSpeedAverageSpeed } from "./PortPredictorDisplayDeparture"
import DatePicker from "react-datepicker"
import moment from "moment"
import { useItineraryContext } from "modules/PortPredictor/hooks/itineraryContext"
import { highlightDays } from "../PortPredictorArrival/PortPredictorArrivalInputs"
import { useFetchPortHolidays } from "modules/PortHolidays/useFetchPortHolidays"
import { getFootnote } from "../PortPredictorDatePickerHelpers"
interface SelectOption {
  label: string
  value: string
}

interface PortPredictorDepartureInputsProps {
  chosenVesselSpeedData: PortPredictorVesselSpeed[] | undefined
  chosenVesselStateData: PortPredictorVesselStateDataWithMajorCargo | undefined
  currMarker: PortPredictorMarkerDeparture
  prevMarker: PortPredictorMarkerArrival | undefined
  onUpdateMarker: (updatedMarker: PortPredictorJourneyMarker) => void
  showSpeedGraphModal: () => void
  isStartOfJourney: boolean
}

const vesselStates = [
  {
    label: VesselStateLadenOrBallast.Laden,
    value: VesselStateLadenOrBallast.Laden,
  },
  {
    label: VesselStateLadenOrBallast.Ballast,
    value: VesselStateLadenOrBallast.Ballast,
  },
]

export function PortPredictorDepartureInputs({
  isStartOfJourney,
  currMarker,
  chosenVesselSpeedData,
  chosenVesselStateData,
  onUpdateMarker,
  showSpeedGraphModal,
  prevMarker,
}: PortPredictorDepartureInputsProps) {
  const { getTabStateKeyValue } = useItineraryContext()
  const speedDataWithAvg = getVesselSpeedDataWithAverages(chosenVesselSpeedData)

  const isChosenVesselSpeedAverageSpeed = getIsChosenVesselSpeedAverageSpeed({
    chosenVesselSpeedData,
    currMarker,
  })

  const defaultMonthsAgoFromToday =
    currMarker.vesselState === VesselStateLadenOrBallast.Laden
      ? speedDataWithAvg?.laden.defaultMonths
      : speedDataWithAvg?.ballast.defaultMonths

  const defaultSpeedKey =
    currMarker.vesselState === VesselStateLadenOrBallast.Laden
      ? speedDataWithAvg?.laden.defaultMonthsKey
      : speedDataWithAvg?.ballast.defaultMonthsKey

  const defaultSpeed =
    currMarker.vesselState === VesselStateLadenOrBallast.Laden
      ? speedDataWithAvg?.laden[defaultSpeedKey as SpeedDataAvgKey]?.avg
      : speedDataWithAvg?.ballast[defaultSpeedKey as SpeedDataAvgKey]?.avg

  const [chosenVesselState, setChosenVesselState] = useState<
    VesselStateLadenOrBallast | undefined
  >(currMarker.vesselState)

  const [chosenVesselSpeed, setChosenVesselSpeed] = useState<string>(
    currMarker.vesselSpeedSelected
      ? String(currMarker.vesselSpeedSelected)
      : String(defaultSpeed)
  )

  const [monthsAgoFromToday, setMonthsAgoFromToday] = useState<
    number | undefined
  >(defaultMonthsAgoFromToday)

  const [weatherMargin, setWeatherMargin] = useState<string>(
    currMarker.vesselSeaMargin ? currMarker.vesselSeaMargin : ""
  )

  const getHolidaysStart = currMarker.date
    ? moment(currMarker.date).startOf("year").format("YYYY-MM-DD")
    : moment().startOf("year").format("YYYY-MM-DD")

  const getHolidaysEnd = currMarker.date
    ? moment(currMarker.date).add(1, "year").endOf("year").format("YYYY-MM-DD")
    : moment().add(1, "year").endOf("year").format("YYYY-MM-DD")

  const { portHolidaysData } = useFetchPortHolidays({
    locode: currMarker.port?.locode,
    startDate: getHolidaysStart,
    endDate: getHolidaysEnd,
  })

  useEffect(() => {
    if (!currMarker.vesselSpeedSelected) {
      return
    }
    setChosenVesselSpeed(String(currMarker.vesselSpeedSelected))
  }, [currMarker.vesselSpeedSelected])

  const handleChangeMonthsAgoFromToday = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.id === undefined) {
      return
    }
    if (
      event.target.id === "0" &&
      chosenVesselStateData &&
      chosenVesselStateData.last_sog
    ) {
      setChosenVesselSpeed(String(chosenVesselStateData.last_sog))
      onUpdateMarker({
        ...currMarker,
        vesselSpeedSelected: chosenVesselStateData.last_sog,
        vesselMonthsAvgSelected: +event.target.id,
        vesselSeaMargin: "",
        preserveFollowingPortType: true,
      })

      // If user selected the current option (the last_sog) we don't proceed and terminate here
      return
    }

    setMonthsAgoFromToday(Number(event.target.id))
    setWeatherMargin("")

    const monthsKey =
      Number(event.target.id) === 3
        ? SpeedDataAvgKey.threeMonths
        : SpeedDataAvgKey.SixMonths

    const newAvgSpeed =
      chosenVesselState === VesselStateLadenOrBallast.Laden
        ? speedDataWithAvg?.laden[monthsKey].avg
        : speedDataWithAvg?.ballast[monthsKey].avg

    if (!newAvgSpeed) {
      setChosenVesselSpeed("")
      return
    }

    onUpdateMarker({
      ...currMarker,
      vesselSpeedSelected: newAvgSpeed,
      vesselMonthsAvgSelected: +event.target.id,
      vesselSeaMargin: "",
      preserveFollowingPortType: true,
    })
    setChosenVesselSpeed(String(newAvgSpeed))
    return
  }

  const isDepartureDateInPast =
    currMarker.date && moment(currMarker.date).isBefore(moment(), "day")
  const showDepartureDateInput =
    (currMarker.id === 1 || isDepartureDateInPast) &&
    currMarker.lastNavStaDesc !== "Under way using engine"

  return (
    <div className="mb-4 PortPredictor_InputsContainer">
      <div className="PortPredictor_DepartureDate mb-2">
        {showDepartureDateInput && (
          <div className="PortPredictor_InputWrapper">
            <Form.Label className="PortPredictor_FormLabel fw-bold">
              <>Departure Date</>
            </Form.Label>
            <div className="PortPredictor_InputGroup PortPredictor_DatePicker_Wrapper">
              <DatePicker
                disabled={!getTabStateKeyValue("isEditMode")}
                dateFormat="dd/MM/yyyy"
                placeholderText="Enter Departure Date"
                selected={currMarker.date ? new Date(currMarker.date) : null}
                dayClassName={(date) =>
                  highlightDays({
                    date,
                    portHolidaysData: portHolidaysData?.holidays,
                    departureDate: currMarker.date,
                    arrivalDate: prevMarker?.arrivalDate,
                  })
                }
                onChange={(date) => {
                  onUpdateMarker({
                    ...currMarker,
                    date: moment(date).format("YYYY-MM-DD"),
                    departureDateManuallyUpdated: true,
                  })
                }}
              >
                <div className="PortPredictor_DatePicker_Footnote_Wrapper">
                  <div>
                    {getFootnote({
                      portHolidays: portHolidaysData?.holidays,
                      date: prevMarker?.arrivalDate
                        ? new Date(prevMarker.arrivalDate)
                        : new Date(),
                    })}
                  </div>
                </div>
              </DatePicker>
            </div>
          </div>
        )}
        {currMarker.id === 1 && isDepartureDateInPast && (
          <div className="fw-bold" style={{ color: "#f14725" }}>
            Adjust date to resolve issue
          </div>
        )}
      </div>
      <div className="PortPredictor_InputWrapper">
        <Form.Label className="PortPredictor_FormLabel fw-bold">
          <div>Vessel State</div>
        </Form.Label>
        <Select
          isDisabled={!getTabStateKeyValue("isEditMode")}
          className="PortPredictor_InputGroup PortPredictor_Select"
          options={vesselStates}
          value={
            chosenVesselState
              ? { label: chosenVesselState, value: chosenVesselState }
              : undefined
          }
          onChange={(vesselState: SingleValue<SelectOption>) => {
            if (!vesselState) {
              return
            }

            let speed
            let sog
            if (
              currMarker.id === 0 &&
              chosenVesselStateData &&
              chosenVesselStateData?.last_nav_sta === "UWAY"
            ) {
              // If vessel is underway, we use sog for the vessel speed
              sog = chosenVesselStateData.last_sog
            }

            const chosenState =
              vesselState.value === VesselStateLadenOrBallast.Laden
                ? VesselStateLadenOrBallast.Laden
                : VesselStateLadenOrBallast.Ballast

            const ladenBallastKey = chosenState.toLocaleLowerCase() as
              | "laden"
              | "ballast"

            // When user switches from laden to ballast (or VV) ballast may not have 3 month data
            // So we have to switch to the default time range that will give avg. speed

            if (speedDataWithAvg) {
              const defaultMonthsKey =
                speedDataWithAvg[ladenBallastKey].defaultMonthsKey
              const defaultMonths =
                speedDataWithAvg[ladenBallastKey].defaultMonths
              setMonthsAgoFromToday(defaultMonths)
              speed = speedDataWithAvg[ladenBallastKey][defaultMonthsKey].avg
            }

            if (!speed) {
              setChosenVesselState(chosenState)
              setChosenVesselSpeed("")
              onUpdateMarker({
                ...currMarker,
                vesselState: chosenState,
                vesselSpeedSelected: undefined,
              })
              return
            }

            onUpdateMarker({
              ...currMarker,
              vesselState: chosenState,
              vesselSpeedSelected: sog ? sog : speed, // prefer using sog as sog will be defined only for underway vessels
              preserveFollowingPortType: false,
            })
            setChosenVesselState(chosenState)
            setChosenVesselSpeed(String(speed))
            setWeatherMargin("")
          }}
        />
      </div>
      <div className="PortPredictor_InputWrapper mt-2">
        <Form.Label className="PortPredictor_FormLabel fw-bold">
          <>Vessel Speed</>
        </Form.Label>
        <InputGroup className="PortPredictor_InputGroup">
          <Form.Control
            disabled={!getTabStateKeyValue("isEditMode")}
            className="ms-0"
            placeholder="Average Speed"
            aria-label="Average Speed"
            aria-describedby="avg-speed"
            value={getRoundedValue(
              isStartOfJourney
                ? currMarker.vesselSpeedSelected + ""
                : chosenVesselSpeed
            )}
            type="number"
            onChange={(event) => {
              if (event.target.value) {
                setChosenVesselSpeed(event.target.value)
                setWeatherMargin("")
                onUpdateMarker({
                  ...currMarker,
                  vesselSpeedSelected: Number(event.target.value),
                  vesselSeaMargin: undefined,
                  preserveFollowingPortType: true,
                })
                return
              }
              setChosenVesselSpeed("")
            }}
          />
          <InputGroup.Text id="avg-speed">Kts.</InputGroup.Text>
        </InputGroup>
        <div className="PortPredictor_InputAlert">
          {currMarker.id === 0 && (
            <OverlayTrigger placement="top" overlay={renderSpeedDisclaimer()}>
              <i
                className="ti-info-alt ms-1"
                style={{ position: "relative" }}
              />
            </OverlayTrigger>
          )}
        </div>
      </div>
      {(chosenVesselSpeed === undefined || chosenVesselSpeed === "") && (
        <div
          className="d-flex justify-content-end"
          style={{ fontSize: "0.6rem" }}
        >
          {`No data with ${chosenVesselState} status in the last ${monthsAgoFromToday} months`}
        </div>
      )}
      {chosenVesselSpeedData && (
        <div className="d-flex justify-content-end">
          <div
            className="PortPredictor_InputWrapper mt-2 justify-content-between"
            style={{ width: "270px" }}
          >
            {[3, 6, 0].map((dateRange) => {
              if (currMarker.id !== 0 && dateRange === 0) {
                // Only show the "current" checkbox for the first marker (which could be UWAY)
                return <span key={dateRange}></span>
              }
              return (
                <div
                  key={dateRange}
                  className="PortPredictor_CheckBoxSelector_ItemWrapper"
                >
                  <Form.Check
                    className="p-0  me-1 PortPredictor_CheckBoxSelector_Item"
                    type="checkbox"
                    id={String(dateRange)}
                    disabled={!getTabStateKeyValue("isEditMode")}
                  >
                    <div className="PortPredictor_CheckBoxSelector_CheckInput">
                      <Form.Check.Input
                        className="p-0 ms-1 mt-0"
                        style={{ float: "revert" }}
                        type="checkbox"
                        checked={(() => {
                          if (
                            currMarker.vesselSpeedSelected ===
                              chosenVesselStateData?.last_sog &&
                            dateRange === 0
                          ) {
                            return true
                          }
                          return (
                            dateRange === monthsAgoFromToday &&
                            isChosenVesselSpeedAverageSpeed
                          )
                        })()}
                        disabled={!getTabStateKeyValue("isEditMode")}
                        onChange={handleChangeMonthsAgoFromToday}
                      />
                      <Form.Check.Label
                        style={{ width: "unset" }}
                        className="m-0 ms-1 mt-0"
                      >
                        {dateRange === 0 ? (
                          <>Current</>
                        ) : (
                          <>{dateRange} mths avg.</>
                        )}
                      </Form.Check.Label>
                    </div>
                  </Form.Check>
                </div>
              )
            })}
          </div>
        </div>
      )}
      <div className="PortPredictor_InputWrapper mt-2 mb-2">
        <Form.Label className="PortPredictor_FormLabel fw-bold">
          <>Sea Margin</>
        </Form.Label>

        <InputGroup className="PortPredictor_InputGroup">
          <Form.Control
            className="ms-0"
            placeholder="Est. margin in %"
            aria-label="Weather Margin"
            aria-describedby="weather-margin"
            disabled={!chosenVesselSpeed || !getTabStateKeyValue("isEditMode")}
            value={getRoundedValue(
              isStartOfJourney ? currMarker.vesselSeaMargin + "" : weatherMargin
            )}
            type="number"
            onChange={(event) => {
              if (!chosenVesselSpeed) {
                return
              }

              // TODO: Extract this and do tests
              const currentVesselSpeed = Number(chosenVesselSpeed)
              const prevWeatherMargin = Number(weatherMargin)

              setWeatherMargin(event.target.value)

              if (event.target.value === "") {
                onUpdateMarker({
                  ...currMarker,
                  vesselSpeedSelected: defaultSpeed,
                  preserveFollowingPortType: true,
                })

                setChosenVesselSpeed(String(defaultSpeed))
              }

              if (event.target.value) {
                const newWeatherMargin = Number(event.target.value)

                if (prevWeatherMargin === 0) {
                  const newBase1 = calcDiscount({
                    base: currentVesselSpeed,
                    percentToDeduct: newWeatherMargin,
                  })

                  onUpdateMarker({
                    ...currMarker,
                    vesselSpeedSelected: newBase1,
                    preserveFollowingPortType: true,
                    vesselSeaMargin: event.target.value,
                  })

                  setChosenVesselSpeed(String(newBase1))
                  return
                }

                const origAmount = calcOriginalAmountFromDiscountedValue({
                  discountedValue: currentVesselSpeed,
                  percentageDeducted: prevWeatherMargin,
                })
                const newBase2 = calcDiscount({
                  base: origAmount,
                  percentToDeduct: newWeatherMargin,
                })

                onUpdateMarker({
                  ...currMarker,
                  vesselSpeedSelected: newBase2,
                  vesselSeaMargin: event.target.value,
                  preserveFollowingPortType: true,
                })

                setChosenVesselSpeed(String(newBase2))
                return
              }

              if (!event.target.value) {
                const origAmount = calcOriginalAmountFromDiscountedValue({
                  discountedValue: currentVesselSpeed,
                  percentageDeducted: prevWeatherMargin,
                })

                onUpdateMarker({
                  ...currMarker,
                  vesselSpeedSelected: origAmount,
                  vesselSeaMargin: event.target.value,
                  preserveFollowingPortType: true,
                })

                setChosenVesselSpeed(String(origAmount))
                return
              }

              setWeatherMargin("")
            }}
          />
          <InputGroup.Text id="avg-speed">%</InputGroup.Text>
        </InputGroup>
      </div>
      <div className="PortPredictor_InputWrapper d-flex mt-1 fs-sm">
        <span className="fs-sm fc-gray">Open Speed Graph</span>
        <Button
          disabled={!getTabStateKeyValue("isEditMode")}
          className="d-flex justify-content-center align-items-center p-1 ms-1"
          onClick={() => showSpeedGraphModal()}
        >
          <i className="ti-fullscreen m-0" style={{ fontSize: "10px" }} />
        </Button>
      </div>
    </div>
  )
}

function calcDiscount({
  base,
  percentToDeduct,
}: {
  base: number
  percentToDeduct: number
}) {
  const percentInDecimals = percentToDeduct / 100
  const multiplyBy = 1 - percentInDecimals
  return base * multiplyBy
}

function calcOriginalAmountFromDiscountedValue({
  discountedValue,
  percentageDeducted,
}: {
  discountedValue: number
  percentageDeducted: number
}) {
  const percentInDecimals = percentageDeducted / 100
  const decimalMultiplier = 1 - percentInDecimals
  return discountedValue / decimalMultiplier
}

function getRoundedValue(weatherMargin: string) {
  if (!weatherMargin) {
    return ""
  }
  return BigNumber(weatherMargin)
    .decimalPlaces(2, BigNumber.ROUND_HALF_EVEN)
    .toString()
}

function renderSpeedDisclaimer() {
  return (
    <Popover id="speed-tooltip">
      <Popover.Body>
        {" "}
        Current speed is based on last recorded SOG. If this is not available,
        use the 3 or 6 mths avg speed, or input manually.{" "}
      </Popover.Body>
    </Popover>
  )
}
